/*
 * Copyright Innov'ATM all rights reserved.
 * This software is the property of Innov'ATM and may not be used in any manner except under a
 * license agreement signed with Innov'ATM.
 */

import { LanguageSetting } from '@types';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { IntlConfig } from 'react-intl/src/types';
import { createContext, useContext, useContextSelector } from 'use-context-selector';

import { defaultLocale, Locales } from '../config/constants';
import { useOptimizedState } from '../hooks/useOptimizedState';
import { noop } from '../utils/data.utils';
import { useCurrentUserSettingsLanguage } from './OidcContext/OidcContext.selectors';

export interface IntlState {
    locale: Locales;
    translations: Record<string, any>;
}

const initialIntlState: IntlState = {
    locale: defaultLocale,
    translations: {},
};

export interface IntlContextState {
    state: IntlState;
    setState: React.Dispatch<React.SetStateAction<IntlState>>;
}

const initialValue: IntlContextState = {
    state: initialIntlState,
    setState: noop,
};

export const IntlContext = createContext<IntlContextState>(initialValue);

export const useIntlContext = () => useContext(IntlContext);

const useIntlSet = () => useContextSelector(IntlContext, state => state.setState);

export const useIntlValue = <Key extends keyof IntlState>(key: Key): IntlState[Key] =>
    useContextSelector(IntlContext, state => state.state[key]);

export const useSetIntl = <Key extends keyof IntlState>(key: Key) => {
    const set = useIntlSet();

    return useCallback(
        (value: React.SetStateAction<IntlState[Key]>) =>
            set(v => {
                const newValue = typeof value === 'function' ? (value as any)(v[key]) : value;

                return {
                    ...v,
                    [key]: newValue,
                };
            }),
        [key, set],
    );
};

export const useSetIntlValue = <Key extends keyof IntlState>(key: Key, value: React.SetStateAction<IntlState[Key]>) => {
    const set = useSetIntl(key);

    return useCallback(() => set(value), [set, value]);
};

export const useIntlState = <Key extends keyof IntlState>(
    key: Key,
): [IntlState[Key], React.Dispatch<React.SetStateAction<IntlState[Key]>>] => [useIntlValue(key), useSetIntl(key)];

export const IntlContextProvider = ({
    children,
    config,
    ...rest
}: {
    children: React.ReactNode;
    config?: Partial<IntlState>;
} & Partial<IntlConfig>) => {
    const [state, setState] = useOptimizedState<IntlState>({ ...initialIntlState, ...config });
    const language = useCurrentUserSettingsLanguage();

    /**
     * Listen to AuthContext language changes to update the locale
     * This was done primarily to set the locale upon login based off current user settings
     */
    useEffect(() => {
        setState(s => ({
            ...s,
            locale:
                language === LanguageSetting.ENGLISH
                    ? Locales.en
                    : language === LanguageSetting.FRENCH
                    ? Locales.fr
                    : defaultLocale,
        }));
    }, [language, setState]);

    return (
        <IntlContext.Provider value={{ state, setState }}>
            <IntlProvider
                key={state.locale}
                locale={state.locale}
                defaultLocale={defaultLocale}
                messages={state.translations[state.locale]}
                {...rest}
            >
                {children}
            </IntlProvider>
        </IntlContext.Provider>
    );
};
